import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, SlidersHorizontal } from 'lucide-react';
import {
	SIGNATURE_EDITOR_CANVAS_HEIGHT_PX,
	SIGNATURE_EDITOR_CANVAS_WIDTH_PX,
	SIGNATURE_INNER_PADDING_PX,
} from './signatureLayoutShared';

type DragTarget = 'signature';
type ResizeTarget = 'signature';

interface ElementLayout {
	x: number;
	y: number;
	width: number;
	height: number;
	rotation: number;
	opacity: number;
	zIndex: number;
}

interface SignatureModel {
	companyName: string;
	textHtml: string;
	textFontFamily: string;
	textFontSize: number;
	documentPadding: number;
	signatureBottomGap: number;
	signature: ElementLayout & {
		src: string;
		lockAspectRatio: boolean;
		anchorOffsetX: number;
		anchorOffsetY: number;
	};
	stamp: ElementLayout & {
		src: string;
		enabled: boolean;
		offsetX: number;
		offsetY: number;
	};
}

interface SignatureLayoutEditorProps {
	valueHtml: string;
	signerName: string;
	designation: string;
	detailsText: string;
	companyName: string;
	onCompanyNameChange: (value: string) => void;
	onChangeHtml: (html: string) => void;
}

const SIGNATURE_FONT_SIZES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24];
const FIXED_SIGNATURE_WIDTH_PX = 149;
const FIXED_SIGNATURE_HEIGHT_PX = 119;
const LEGACY_SIGNATURE_COMPANY_NAME = 'Surbhi Enterprises Pvt. Ltd.';
const DEFAULT_SIGNATURE_COMPANY_NAME = 'Surbhi Electronet Pvt. Ltd.';

const normalizeCompanyName = (value?: string | null) => {
	const company = String(value || '').trim();
	if (!company || company === LEGACY_SIGNATURE_COMPANY_NAME) return DEFAULT_SIGNATURE_COMPANY_NAME;
	return company;
};

const defaultModel = (): SignatureModel => ({
	companyName: DEFAULT_SIGNATURE_COMPANY_NAME,
	textHtml: '',
	textFontFamily: 'Arial',
	textFontSize: 12,
	documentPadding: 36,
	signatureBottomGap: 24,
	signature: {
		src: '',
		x: 0,
		y: 0,
		width: FIXED_SIGNATURE_WIDTH_PX,
		height: FIXED_SIGNATURE_HEIGHT_PX,
		rotation: -1,
		opacity: 0.93,
		zIndex: 20,
		lockAspectRatio: true,
		anchorOffsetX: 0,
		anchorOffsetY: 0,
	},
	stamp: {
		src: '',
		enabled: false,
		x: 350,
		y: 160,
		width: 140,
		height: 140,
		rotation: -4,
		opacity: 0.72,
		zIndex: 5,
		offsetX: -36,
		offsetY: -10,
	},
});

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeEditableHtml = (rawHtml: string) => {
	const value = String(rawHtml || '').trim();
	if (!value || value === '<br>' || value === '<div><br></div>') {
		return '<p><br></p>';
	}
	return value;
};

const readImageDataUrl = (file: File) => new Promise<{ src: string; width: number; height: number }>((resolve, reject) => {
	const reader = new FileReader();
	reader.onload = () => {
		const rawSrc = String(reader.result || '');
		const img = new Image();
		img.onload = () => {
			const width = img.width || 1;
			const height = img.height || 1;
			const maxDim = 1000;
			if (width <= maxDim && height <= maxDim) {
				resolve({ src: rawSrc, width, height });
				return;
			}
			const ratio = Math.min(maxDim / width, maxDim / height);
			const canvas = document.createElement('canvas');
			canvas.width = Math.round(width * ratio);
			canvas.height = Math.round(height * ratio);
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				resolve({ src: rawSrc, width, height });
				return;
			}
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
			const compressed = canvas.toDataURL(file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.86);
			resolve({ src: compressed, width: canvas.width || 1, height: canvas.height || 1 });
		};
		img.onerror = () => reject(new Error('Could not read image dimensions'));
		img.src = rawSrc;
	};
	reader.onerror = () => reject(new Error('Could not read image file'));
	reader.readAsDataURL(file);
});

const generateSignatureHtml = (model: SignatureModel) => {
	const textHtml = normalizeEditableHtml(model.textHtml || '');
	const metadata = encodeURIComponent(JSON.stringify({
		...model,
		referenceSystem: {
			width: SIGNATURE_EDITOR_CANVAS_WIDTH_PX,
			height: SIGNATURE_EDITOR_CANVAS_HEIGHT_PX,
			unit: 'px',
		},
		signatureBox: {
			width: model.signature.width,
			height: model.signature.height,
			unit: 'px',
			leftPct: Number(((model.signature.x / SIGNATURE_EDITOR_CANVAS_WIDTH_PX) * 100).toFixed(4)),
			topPct: Number(((model.signature.y / SIGNATURE_EDITOR_CANVAS_HEIGHT_PX) * 100).toFixed(4)),
			widthPct: Number(((model.signature.width / SIGNATURE_EDITOR_CANVAS_WIDTH_PX) * 100).toFixed(4)),
			heightPct: Number(((model.signature.height / SIGNATURE_EDITOR_CANVAS_HEIGHT_PX) * 100).toFixed(4)),
		},
		signature: { ...model.signature, src: '' },
		stamp: { ...model.stamp, src: '' },
	}));

	return `
		<div data-signature-model="${metadata}" style="display:none"></div>
		<div style="position:relative;display:inline-block;background:transparent;padding:0;margin:0;font-family:Arial,sans-serif;color:#111827;width:${SIGNATURE_EDITOR_CANVAS_WIDTH_PX - (model.documentPadding * 2)}px;min-height:180px;overflow:visible;">
			${model.signature.src ? `<img src="${model.signature.src}" alt="signature" style="position:absolute;left:${model.signature.x}px;top:${model.signature.y}px;width:${model.signature.width}px;height:${model.signature.height}px;transform:rotate(${model.signature.rotation}deg);opacity:${model.signature.opacity};z-index:${Math.max(model.signature.zIndex || 0, 11)};object-fit:contain;margin:0;padding:${SIGNATURE_INNER_PADDING_PX}px;box-sizing:border-box;overflow:visible;" />` : ''}
			<div style="position:relative;z-index:10;margin:0;padding:0;font-family:Arial,sans-serif;font-size:${model.textFontSize || 12}px;line-height:1.5;white-space:pre-wrap;margin-top:130px;">${textHtml}</div>
		</div>
	`;
};

const parseModelFromHtml = (html: string): SignatureModel | null => {
	if (!html) return null;
	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');
		const marker = doc.querySelector('[data-signature-model]');
		const raw = marker?.getAttribute('data-signature-model');
		const parsed = raw ? JSON.parse(decodeURIComponent(raw)) : null;

		const allImgs = Array.from(doc.querySelectorAll('img')) as HTMLImageElement[];
		const signatureImg = allImgs.find((img) => String(img.alt || '').toLowerCase().includes('signature')) || allImgs[0] || null;
		const stampImg = allImgs.find((img) => String(img.alt || '').toLowerCase().includes('stamp')) || null;
		const textContainer = doc.querySelector('div[style*="line-height:1.5"]');

		const merged = { ...defaultModel(), ...(parsed || {}) } as SignatureModel;
		merged.companyName = normalizeCompanyName(merged.companyName);
		merged.textFontFamily = 'Arial';
		merged.signature = {
			...defaultModel().signature,
			...(parsed?.signature || {}),
			src: signatureImg?.src || '',
		};
		merged.stamp = {
			...defaultModel().stamp,
			...(parsed?.stamp || {}),
			src: stampImg?.src || '',
		};
		if (textContainer) {
			merged.textHtml = normalizeEditableHtml(textContainer.innerHTML || '');
		}
		return merged;
	} catch {
		return null;
	}
};

export function SignatureLayoutEditor({
	valueHtml,
	signerName: _signerName,
	designation: _designation,
	detailsText: _detailsText,
	companyName,
	onCompanyNameChange,
	onChangeHtml,
}: SignatureLayoutEditorProps) {
	const [model, setModel] = useState<SignatureModel>(() => defaultModel());
	const [activeDrag, setActiveDrag] = useState<{ target: DragTarget; startX: number; startY: number; originX: number; originY: number } | null>(null);
	const signatureInputRef = useRef<HTMLInputElement>(null);
	const editableRef = useRef<HTMLDivElement>(null);
	const lastGeneratedRef = useRef<string>('');

	useEffect(() => {
		const parsed = parseModelFromHtml(valueHtml);
		if (parsed && valueHtml !== lastGeneratedRef.current) {
			setModel((prev) => ({
				...prev,
				...parsed,
				textFontFamily: 'Arial',
				signature: {
					...prev.signature,
					...parsed.signature,
					width: FIXED_SIGNATURE_WIDTH_PX,
					height: FIXED_SIGNATURE_HEIGHT_PX,
				},
			}));
			const parsedCompanyName = normalizeCompanyName(parsed.companyName);
			if (parsedCompanyName && parsedCompanyName !== companyName) {
				onCompanyNameChange(parsedCompanyName);
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [valueHtml]);

	useEffect(() => {
		setModel((prev) => ({ ...prev, companyName: normalizeCompanyName(companyName || prev.companyName) }));
	}, [companyName]);

	useEffect(() => {
		if (model.textFontFamily !== 'Arial') {
			setModel((prev) => ({ ...prev, textFontFamily: 'Arial' }));
		}
	}, [model.textFontFamily]);

	const html = useMemo(() => generateSignatureHtml(model), [model]);

	useEffect(() => {
		lastGeneratedRef.current = html;
		onChangeHtml(html);
	}, [html, onChangeHtml]);

	useEffect(() => {
		const onMouseMove = (event: MouseEvent) => {
			if (activeDrag) {
				const dx = event.clientX - activeDrag.startX;
				const dy = event.clientY - activeDrag.startY;
				setModel((prev) => ({
					...prev,
					signature: {
						...prev.signature,
						x: clamp(activeDrag.originX + dx, 0, Math.max(0, SIGNATURE_EDITOR_CANVAS_WIDTH_PX - prev.signature.width)),
						y: clamp(activeDrag.originY + dy, 0, Math.max(0, SIGNATURE_EDITOR_CANVAS_HEIGHT_PX - prev.signature.height)),
					},
				}));
			}

		};

		const onMouseUp = () => {
			setActiveDrag(null);
		};

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		};
	}, [activeDrag]);

	const uploadSignature = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		if (!file.type.startsWith('image/')) {
			alert('Please select a valid image file for the signature.');
			event.target.value = '';
			return;
		}
		try {
			const { src } = await readImageDataUrl(file);
			setModel((prev) => ({
				...prev,
				signature: {
					...prev.signature,
					src,
					width: FIXED_SIGNATURE_WIDTH_PX,
					height: FIXED_SIGNATURE_HEIGHT_PX,
				},
			}));
		} catch (err) {
			console.error('Failed to read signature image:', err);
			alert('Failed to load the selected image. Please try a different file.');
		} finally {
			event.target.value = '';
		}
	};

	return (
		<div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 md:p-5 space-y-4 overflow-hidden">
			<div className="rounded-xl border border-gray-200 bg-gray-50 p-3 overflow-auto">
				<div className="flex flex-wrap items-end gap-3">
					<div className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide mr-2">
						<ImagePlus className="w-3.5 h-3.5" /> Signature Tools
					</div>
					<button className="px-3 py-2 rounded-lg bg-violet-600 text-white text-sm hover:bg-violet-700" onClick={() => signatureInputRef.current?.click()}>Upload Signature</button>
					{model.signature.src && (
						<button
							type="button"
							onClick={() => setModel((prev) => ({ ...prev, signature: { ...prev.signature, src: '', width: FIXED_SIGNATURE_WIDTH_PX, height: FIXED_SIGNATURE_HEIGHT_PX } }))}
							className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm hover:bg-red-100"
						>
							Remove
						</button>
					)}
					<input ref={signatureInputRef} type="file" accept="image/*" className="hidden" onChange={uploadSignature} />

					<div className="h-7 w-px bg-gray-300 mx-1" />

					<div className="text-sm text-gray-700">Size: {FIXED_SIGNATURE_WIDTH_PX}×{FIXED_SIGNATURE_HEIGHT_PX}px</div>
					<label className="text-sm text-gray-700">Rotate
						<input type="range" min={-2} max={2} step={0.1} value={model.signature.rotation} onChange={(e) => setModel((prev) => ({ ...prev, signature: { ...prev.signature, rotation: Number(e.target.value) } }))} className="ml-2 w-24 align-middle" />
					</label>
					<label className="text-sm text-gray-700">Opacity
						<input type="range" min={0.9} max={0.95} step={0.01} value={model.signature.opacity} onChange={(e) => setModel((prev) => ({ ...prev, signature: { ...prev.signature, opacity: Number(e.target.value) } }))} className="ml-2 w-24 align-middle" />
					</label>
					<label className="text-sm text-gray-700">Layer
						<input type="range" min={1} max={20} step={1} value={model.signature.zIndex} onChange={(e) => setModel((prev) => ({ ...prev, signature: { ...prev.signature, zIndex: Number(e.target.value) } }))} className="ml-2 w-20 align-middle" />
					</label>

					<div className="h-7 w-px bg-gray-300 mx-1" />
					<div className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
						<SlidersHorizontal className="w-3.5 h-3.5" /> Text
					</div>
					<label className="text-sm text-gray-700">Font Size
						<select
							value={model.textFontSize}
							onChange={(e) => setModel((prev) => ({ ...prev, textFontSize: Number(e.target.value) }))}
							className="ml-2 border border-gray-300 rounded px-2 py-1 bg-white text-sm text-gray-700"
						>
							{SIGNATURE_FONT_SIZES.map((s) => (
								<option key={s} value={s}>{s}px</option>
							))}
						</select>
					</label>
				</div>
			</div>

			<div className="overflow-hidden">
				<div className="rounded-xl border border-gray-200 bg-[#f3f3f1] p-3">
					<div className="signature-canvas-container relative bg-[#fcfcfa] border border-gray-300 shadow-inner mx-auto inline-block" style={{ width: SIGNATURE_EDITOR_CANVAS_WIDTH_PX, height: SIGNATURE_EDITOR_CANVAS_HEIGHT_PX }}>
						<div className="absolute inset-0 bg-gradient-to-b from-white/60 to-[#f8f8f6]/95" />

						<div className="relative z-10 w-full h-full" >
							<div
								className={`absolute ${model.signature.src ? 'cursor-move' : ''} border border-dashed rounded-md ${model.signature.src ? 'border-violet-400 bg-violet-50/15' : 'border-gray-300 bg-gray-50/80'}`}
								style={{
									left: model.signature.x,
									top: model.signature.y,
									width: model.signature.width,
									height: model.signature.height,
									transform: `rotate(${model.signature.rotation}deg)`,
									opacity: model.signature.src ? model.signature.opacity : 1,
									zIndex: Math.max(model.signature.zIndex || 0, 11),
								}}
								onMouseDown={(event) => {
									if (!model.signature.src) return;
										event.preventDefault();
										setActiveDrag({
											target: 'signature',
											startX: event.clientX,
											startY: event.clientY,
											originX: model.signature.x,
											originY: model.signature.y,
										});
									}}
								>
									{model.signature.src ? (
										<>
											<img src={model.signature.src} alt="signature" className="w-full h-full object-contain select-none pointer-events-none" draggable={false} style={{ padding: `${SIGNATURE_INNER_PADDING_PX}px`, boxSizing: 'border-box' }} />
										</>
									) : (
										<div className="w-full h-full flex items-center justify-center text-[11px] font-medium text-gray-500 px-3 text-center">
											Upload signature to replace this placeholder
										</div>
									)}
								</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
